import { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import StreamBlockParser from "../../../../../../../utils/StreamBlockParser";
import RenderMarkdown from "../RenderMarkdown";
import DynamicChart from "../../../../../../../components/DynamicChart";
import ThinkingBlock from "./ThinkingBlock";
import ToolCallBlock from "./ToolCallBlock";
import FileBlock from "./FileBlock";
import IsolationChecklistBlock from "./IsolationChecklistBlock";
import Toolcalls from "./Toolcalls";

// Create global citation map by scanning only text blocks for citations
export function createGlobalCitationMap(text) {
  const citationMap = new Map();
  let sequentialNumber = 1;

  // 1) Handle markdown links with cite: or c_id: prefix
  const mdCitationRegex =
    /\[([0-9]+)\]\(((?:cite|c_id):(?!\))(?:excel:)?[^)]+)\)/g;
  let mdMatch;
  while ((mdMatch = mdCitationRegex.exec(text)) !== null) {
    const href = mdMatch[2]; // e.g., cite:77015, c_id:77015, or cite:excel:workbook:sheet:range
    const id = href.replace(/^(?:cite|c_id):/, "");
    const type = id.startsWith("excel:") ? "excel-citation" : "citation";
    if (!citationMap.has(id)) {
      citationMap.set(id, { number: sequentialNumber++, type, id });
    }
  }

  // 2) Handle simple [citationId] format (without cite: prefix)
  const simpleCitationRegex = /\[([^\]]+)\](?!\()/g;
  let simpleMatch;
  while ((simpleMatch = simpleCitationRegex.exec(text)) !== null) {
    const id = simpleMatch[1];
    // Skip if it contains spaces (likely regular markdown text) or already exists
    if (!id.includes(" ") && !citationMap.has(id)) {
      const type = id.startsWith("excel:") ? "excel-citation" : "citation";
      citationMap.set(id, { number: sequentialNumber++, type, id });
    }
  }

  // 3) Backward compatibility: support legacy <citation> tags if present
  const blocks = StreamBlockParser.parseComplete(text);
  const legacyRegex = /<(citation|excel-citation)\s+([^>]*?)>(.*?)<\/\1>/gs;
  blocks.forEach((block) => {
    if (block.type === "text" && block.content) {
      let m;
      legacyRegex.lastIndex = 0;
      while ((m = legacyRegex.exec(block.content)) !== null) {
        const tagType = m[1];
        const attrs = m[2];
        const idMatch = attrs.match(/id\s*=\s*["']([^"']+)["']/);
        const legacyId = idMatch ? idMatch[1] : null;
        if (legacyId && !citationMap.has(legacyId)) {
          citationMap.set(legacyId, {
            number: sequentialNumber++,
            type: tagType,
            id: legacyId,
          });
        }
      }
    }
  });

  return { citationMap };
}

function ParseAnswer({
  answer,
  setSelectedSource,
  isStreaming = false,
  selectedFileFromStream,
  onSelectedFileFromStreamChange,
  isolationMarkup,
  onIsolationMarkupChange,
  selectedIsolationMarkup,
  onSelectedIsolationMarkupChange,
  lastStreamedMessageId,
}) {
  const blocks = StreamBlockParser.parseComplete(
    answer["content_object"]["text"]
  );

  // Create global citation map for consistent numbering across all blocks
  const { citationMap } = useMemo(() => {
    return isStreaming
      ? new Map()
      : createGlobalCitationMap(answer["content_object"]["text"]);
  }, [answer["content_object"]["text"], isStreaming]);


  // Group blocks by whether type is tool_call or not, in order
  const groupedBlocks = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    blocks.forEach((block, index) => {
      const isToolCallBlock = block.type === "tool_call";

      if (!currentGroup || currentGroup.isToolCall !== isToolCallBlock) {
        // Start a new group
        currentGroup = {
          isToolCall: isToolCallBlock,
          blocks: [{ block, originalIndex: index }],
        };
        groups.push(currentGroup);
      } else {
        // Add to current group
        currentGroup.blocks.push({ block, originalIndex: index });
      }
    });

    return groups;
  }, [blocks]);

  return (
    <Box maxWidth={"100%"} overflow={"auto"}>
      {answer["error"] && answer["error"] === "stream_api_fail" ? (
        <Typography>
          There was a problem generating this answer. Please try again.
        </Typography>
      ) : (
        <Box>
          {groupedBlocks.map((group, groupIndex) => {
            const hasNextGroup = groupIndex + 1 < groupedBlocks.length;

            return group.isToolCall ? (
              <Toolcalls
                key={`group-${groupIndex}`}
                blocks={group.blocks}
                isStreaming={isStreaming}
                hasNextGroup={hasNextGroup}
              />
            ) : (
              <Box key={`group-${groupIndex}`}>
                {group.blocks.map(({ block, originalIndex }) => {
                  if (block.type === "thinking") {
                    return (
                      <ThinkingBlock
                        key={originalIndex}
                        content={block.content}
                        status={block.status}
                      />
                    );
                  }
                  if (block.type === "tool_call") {
                    return null;
                  }
                  if (block.type === "file") {
                    return (
                      <FileBlock
                        key={originalIndex}
                        block={block}
                        selectedFileFromStream={selectedFileFromStream}
                        onSelectedFileFromStreamChange={
                          onSelectedFileFromStreamChange
                        }
                      />
                    );
                  }
                  if (block.type === "tool_result") {
                    return null;
                  }
                  if (block.type === "chart") {
                    return (
                      <DynamicChart key={originalIndex} config={block.config} />
                    );
                  }
                  if (block.type === "text") {
                    return (
                      <Box key={originalIndex} mb={2}>
                        <RenderMarkdown
                          answer={answer}
                          message={block.content}
                          citationMap={citationMap}
                          setSelectedSource={setSelectedSource}
                        />
                      </Box>
                    );
                  }
                  if (block.type === "IsolationChecklist") {
                    return (
                      <IsolationChecklistBlock
                        key={originalIndex}
                        isStreaming={isStreaming}
                        block={block}
                        isolationMarkup={isolationMarkup}
                        onIsolationMarkupChange={onIsolationMarkupChange}
                        selectedIsolationMarkup={selectedIsolationMarkup}
                        onSelectedIsolationMarkupChange={
                          onSelectedIsolationMarkupChange
                        }
                        lastStreamedMessageId={lastStreamedMessageId}
                        answer={answer}
                      />
                    );
                  }
                  return (
                    <Box key={originalIndex} mb={2}>
                      <RenderMarkdown
                        answer={answer}
                        message={block.content}
                        citationMap={citationMap}
                        setSelectedSource={setSelectedSource}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default ParseAnswer;
